import { forwardRef } from 'react';

/**
 * Shared Button component (design-system Phase 2).
 * Not yet imported by any existing page — safe to iterate on.
 *
 * Props:
 *  - variant: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'success'
 *  - size: 'sm' | 'md' | 'lg'
 *  - fullWidth: boolean (opt-in mobile full-width, never forced)
 *  - loading: boolean (shows inline spinner, sets aria-busy, disables button)
 *  - as: element/component to render instead of <button> (e.g. react-router's
 *    Link, for nav-styled-as-button cases). When set, `type`/`disabled` are
 *    not forwarded (most non-button elements don't support them the same way).
 */
const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    type = 'button',
    as: Component,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const classes = [
    'ds-btn',
    `ds-btn-${variant}`,
    `ds-btn-${size}`,
    fullWidth ? 'ds-btn-full' : '',
    loading ? 'ds-btn-loading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (Component) {
    return (
      <Component ref={ref} className={classes} aria-busy={loading || undefined} {...rest}>
        {loading && <span className="ds-btn-spinner" aria-hidden="true" />}
        <span className="ds-btn-label">{children}</span>
      </Component>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="ds-btn-spinner" aria-hidden="true" />}
      <span className="ds-btn-label">{children}</span>
    </button>
  );
});

export default Button;
